import { useEffect } from 'react';
import { FileServiceCapability, Specs } from '@doxyme/extensions-bridge';
import { useAddCapability } from '@doxyme/extensions-bridge-react';
import { api } from '../../../services/fileService/operations.generated';
import { bridge } from '../../../features/extensions/instances';
import { subscribeToMessage } from '../../../features/realtime/realtime.subscribe';
import { RealtimeMessageType } from '../../../features/realtime/realtime.types';
import { useAppDispatch } from '../../useReduxToolkit';

const { endpoints } = api;

type FileDescriptor = Specs.FileService.FileDescriptor;
type FileMetadataResult = Specs.FileService.FileMetadataResult;
type DeleteFileResult = Specs.FileService.DeleteFileResult;
type ServiceUrlResult = Specs.FileService.ServiceUrlResult;

type FileServiceRealtimePayload = {
  eventName: string;
  eventData: Specs.FileService.UploadStatusPayload;
};

function toGraphQlDescriptor(file: FileDescriptor) {
  return file.isPublic
    ? { fileName: file.fileName, contentType: file.contentType, isPublic: true }
    : {
        fileName: file.fileName,
        contentType: file.contentType,
        isPublic: false,
        ttl: file.ttl,
      };
}

export function useExtensionsFileService(): void {
  const dispatch = useAppDispatch();
  const fileService = useAddCapability(
    bridge,
    () => new FileServiceCapability(),
  );

  useEffect(() => {
    const unsubUploadLinks = fileService.onRequestUploadLinks(
      async ({ clientId, files }) => {
        const response = await dispatch(
          endpoints.fileServiceGetUploadLinks.initiate({
            input: {
              clientId,
              files: files.map(toGraphQlDescriptor),
            },
          }),
        );

        if ('error' in response || !response.data) {
          throw new Error('Failed to get upload links');
        }

        return response.data.FileService.getUploadLinks;
      },
    );

    const unsubMetadata = fileService.onGetFileMetadata(
      async ({ fileUuids }) => {
        const response = await dispatch(
          endpoints.fileServiceGetFileMetadata.initiate(
            { input: { fileUuids } },
            { forceRefetch: true },
          ),
        );

        if ('error' in response || !response.data) {
          throw new Error('Failed to get file metadata');
        }

        return response.data.FileService
          .getFileMetadata as FileMetadataResult[];
      },
    );

    const unsubDelete = fileService.onDeleteFiles(async ({ fileUuids }) => {
      const response = await dispatch(
        endpoints.fileServiceDeleteFiles.initiate({
          input: { fileUuids },
        }),
      );

      if ('error' in response || !response.data) {
        throw new Error('Failed to delete files');
      }

      return response.data.FileService.deleteFiles as DeleteFileResult[];
    });

    const unsubServiceUrl = fileService.onGetServiceUrl(async () => {
      const response = await dispatch(
        endpoints.fileServiceGetServiceUrl.initiate(undefined, {
          forceRefetch: true,
        }),
      );

      if ('error' in response || !response.data) {
        throw new Error('Failed to get service URL');
      }

      return response.data.FileService.getServiceUrl as ServiceUrlResult;
    });

    const unsubRealtime = subscribeToMessage({
      messageType: RealtimeMessageType.FileServiceUpload,
      callback: (message) => {
        const payload = message.payload as
          | FileServiceRealtimePayload
          | undefined;

        if (payload?.eventName === 'uploadStatus' && payload.eventData) {
          fileService.notifyUploadStatus(payload.eventData);
        }
      },
    });

    return () => {
      unsubUploadLinks();
      unsubMetadata();
      unsubDelete();
      unsubServiceUrl();
      unsubRealtime();
    };
  }, [dispatch, fileService]);
}
